import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, ExternalLink, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, ZONES } from "@/data/ideas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = { cat: "mobilita", zona: "tutta", t: "", d: "" };

function IdeaFormDialog({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) setForm(initial ? { cat: initial.cat, zona: initial.zona || "tutta", t: initial.t, d: initial.d } : EMPTY);
  }, [open, initial]);

  const save = async () => {
    if (!form.t.trim() || !form.d.trim()) {
      toast.error("Compila titolo e descrizione.");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="idea-form-dialog"
        className="rounded-none border-2 border-[#0a0d14] bg-[#f4f4f2] max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tighter text-2xl text-[#0a0d14]">
            {isEdit ? `Modifica idea ${String(initial.n).padStart(3, "0")}` : "Nuova idea"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <label className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50">
              Tema
            </label>
            <Select value={form.cat} onValueChange={(v) => setForm((f) => ({ ...f, cat: v }))}>
              <SelectTrigger data-testid="idea-cat-select" className="rounded-none border-[#0a0d14]/30 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50">
              Circoscrizione
            </label>
            <Select value={form.zona} onValueChange={(v) => setForm((f) => ({ ...f, zona: v }))}>
              <SelectTrigger data-testid="idea-zona-select" className="rounded-none border-[#0a0d14]/30 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {ZONES.map((z) => (
                  <SelectItem key={z.key} value={z.key}>
                    {z.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50">
              Titolo
            </label>
            <input
              data-testid="idea-title-input"
              value={form.t}
              onChange={(e) => setForm((f) => ({ ...f, t: e.target.value }))}
              className="w-full mt-1 bg-transparent border-b-2 border-[#0a0d14]/25 py-2 text-lg font-semibold text-[#0a0d14] focus:outline-none focus:border-[#d9381e] transition-colors"
            />
          </div>

          <div>
            <label className="font-mono-num uppercase text-[11px] tracking-widest text-[#0a0d14]/50">
              Descrizione
            </label>
            <textarea
              data-testid="idea-desc-input"
              rows={3}
              value={form.d}
              onChange={(e) => setForm((f) => ({ ...f, d: e.target.value }))}
              className="w-full mt-1 bg-transparent border-b-2 border-[#0a0d14]/25 py-2 text-base text-[#0a0d14] resize-none focus:outline-none focus:border-[#d9381e] transition-colors"
            />
          </div>
        </div>

        <DialogFooter>
          <button
            data-testid="idea-save-btn"
            onClick={save}
            disabled={saving}
            className="w-full bg-[#0d2c54] text-white py-3 font-display font-bold uppercase tracking-wide hover:bg-[#d9381e] transition-colors disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea idea"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("tutte");
  const [zonaFilter, setZonaFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/ideas")
      .then((r) => setIdeas(r.data))
      .catch(() => toast.error("Errore nel caricamento delle idee."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    return ideas.filter((i) => {
      const okCat = catFilter === "tutte" || i.cat === catFilter;
      const okZona = zonaFilter === "all" || i.zona === zonaFilter;
      const okQ =
        !query ||
        i.t.toLowerCase().includes(query.toLowerCase()) ||
        i.d.toLowerCase().includes(query.toLowerCase());
      return okCat && okZona && okQ;
    });
  }, [ideas, catFilter, zonaFilter, query]);

  const handleSave = async (form) => {
    if (editing?.id) {
      const { data } = await api.put(`/ideas/${editing.id}`, form);
      setIdeas((list) => list.map((i) => (i.id === data.id ? data : i)));
      toast.success("Idea aggiornata.");
    } else {
      const { data } = await api.post("/ideas", form);
      setIdeas((list) => [...list, data].sort((a, b) => a.n - b.n));
      toast.success("Idea aggiunta.");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/ideas/${deleteTarget.id}`);
      setIdeas((list) => list.filter((i) => i.id !== deleteTarget.id));
      toast.success("Idea eliminata.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (idea) => {
    setEditing(idea);
    setDialogOpen(true);
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#f4f4f2] text-[#0a0d14]">
      <div className="grain" aria-hidden="true" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0d2c54] text-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono-num text-xs text-[#d9381e] font-bold">100</span>
            <span className="font-display font-extrabold uppercase tracking-tight text-sm md:text-base">
              Gestione Idee
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="hidden md:inline font-mono-num text-[11px] tracking-widest text-white/50">
              {user?.email}
            </span>
            <Link
              to="/"
              data-testid="view-site-link"
              className="inline-flex items-center gap-1.5 font-mono-num uppercase text-[11px] tracking-widest text-white/70 hover:text-white transition-colors"
            >
              <ExternalLink className="h-4 w-4" /> Sito
            </Link>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="inline-flex items-center gap-1.5 bg-[#d9381e] px-3 py-1.5 font-mono-num uppercase text-[11px] tracking-widest hover:bg-white hover:text-[#0d2c54] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-none">
              Le tue idee
            </h1>
            <p className="mt-2 font-mono-num uppercase text-xs tracking-widest text-[#0a0d14]/50">
              {ideas.length} idee totali · {filtered.length} visualizzate
            </p>
          </div>
          <button
            data-testid="add-idea-btn"
            onClick={openNew}
            className="inline-flex items-center justify-center gap-2 bg-[#0d2c54] text-white px-5 py-3 font-display font-bold uppercase tracking-wide hover:bg-[#d9381e] transition-colors"
          >
            <Plus className="h-5 w-5" /> Aggiungi idea
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0a0d14]/40" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca un'idea…"
              className="w-full bg-white border border-[#0a0d14]/20 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#0d2c54]"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="admin-cat-filter" className="rounded-none border-[#0a0d14]/20 bg-white w-full sm:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="tutte">Tutti i temi</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={zonaFilter} onValueChange={setZonaFilter}>
            <SelectTrigger data-testid="admin-zona-filter" className="rounded-none border-[#0a0d14]/20 bg-white w-full sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tutte le circoscrizioni</SelectItem>
              {ZONES.map((z) => (
                <SelectItem key={z.key} value={z.key}>
                  {z.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <p className="font-mono-num uppercase text-xs tracking-widest text-[#0a0d14]/50 py-10">
            Caricamento…
          </p>
        ) : (
          <div className="border border-[#0a0d14]/12 divide-y divide-[#0a0d14]/10 bg-white">
            {filtered.map((idea) => {
              const cat = CATEGORIES.find((c) => c.key === idea.cat);
              const zone = ZONES.find((z) => z.key === idea.zona);
              return (
                <div
                  key={idea.id}
                  data-testid={`admin-idea-${idea.n}`}
                  className="flex items-center gap-4 p-4 hover:bg-[#f4f4f2] transition-colors"
                >
                  <span className="font-mono-num text-xl font-bold text-[#d9381e] w-12 shrink-0">
                    {String(idea.n).padStart(3, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-lg leading-tight truncate">{idea.t}</h3>
                    <p className="text-sm text-[#0a0d14]/60 truncate">{idea.d}</p>
                    <span className="font-mono-num uppercase text-[10px] tracking-widest text-[#0d2c54]/70">
                      {cat?.short} <span className="text-[#0a0d14]/40">◦ {zone?.short}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`edit-idea-${idea.n}`}
                      onClick={() => openEdit(idea)}
                      className="h-9 w-9 flex items-center justify-center border border-[#0a0d14]/20 hover:bg-[#0d2c54] hover:text-white transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`delete-idea-${idea.n}`}
                      onClick={() => setDeleteTarget(idea)}
                      className="h-9 w-9 flex items-center justify-center border border-[#0a0d14]/20 hover:bg-[#d9381e] hover:text-white transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="p-8 text-center font-mono-num uppercase text-xs tracking-widest text-[#0a0d14]/50">
                Nessuna idea trovata.
              </p>
            )}
          </div>
        )}
      </main>

      <IdeaFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSave={handleSave}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog" className="rounded-none border-2 border-[#0a0d14] bg-[#f4f4f2]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black uppercase tracking-tighter text-2xl">
              Eliminare l'idea?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#0a0d14]/70">
              Stai per eliminare "{deleteTarget?.t}". L'azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-cancel-btn">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={confirmDelete}
              className="rounded-none bg-[#d9381e] hover:bg-[#0d2c54]"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
